import { createAuthGuard } from '@auth0/auth0-vue';

const AccountsPage = () => import('@/views/AccountsPage.vue');
const AuthenticationStatusPage = () => import('@/views/AuthenticationStatusPage.vue');
const ManageAppView = () => import('@/views/ManageAppView.vue');
const ProServicesView = () => import('@/views/ProServicesView.vue');
const QuickAddView = () => import('@/views/QuickAddView.vue');
const EditSubscriptionView = () => import('@/views/EditSubscriptionView.vue');
const LearnPage = () => import('@/views/LearnPage.vue');
const AuthorizePage = () => import('@/views/AuthorizePage.vue');
const IntegrationAuthorizePage = () => import('@/views/IntegrationAuthorize.vue');
const AuthErrorPage = () => import('@/views/AuthErrorPage.vue');
const PageNotFound = () => import('@/views/PageNotFound.vue');
const Invite = () => import('@/views/Invite.vue');
const Signup = () => import('@/views/InviteSignup.vue');
const InvitePasswordInstructions = () => import('@/views/InvitePasswordInstructions.vue');
const MfaInvite = () => import('@/views/MfaInvite.vue');
const LinkInvite = () => import('@/views/LinkInvite.vue');
const ThryvIdPage = () => import('@/views/ThryvIdPage.vue');
const LegacyLinkRedirect = () => import('@/views/LegacyLinkRedirect.vue');

import { DEFAULT_ACCOUNT_PAGE_SIZE } from '@/store/accounts/state';

export const createRoutes = (auth, store) => {
    const authGuard = createAuthGuard(auth);

    return [
        {
            path: '/',
            name: 'home',
            redirect: '/accounts',
        },
        {
            path: '/authentication-status',
            name: 'authenticationStatus',
            component: AuthenticationStatusPage,
            meta: {
                title: 'authentication.statusTitle',
            },
            beforeEnter: authGuard,
        },
        {
            path: '/appCheck',
            name: 'loading',
            beforeEnter: async (to, from, next) => {
                // First ensure authentication
                await authGuard(to, from, next);
                await store.dispatch('accounts/LOAD_ACCOUNTS', {
                    pageSize: DEFAULT_ACCOUNT_PAGE_SIZE,
                    searchText: null,
                    orderBy: ['account.legacyId:ASC'],
                    reload: true,
                });

                const appName = store.getters['accounts/getOnlyApp'];

                if (appName !== '' && appName !== undefined) {
                    window.location.assign(`${import.meta.env.VITE_KEAP_URL}/dashboard?app_id=${appName}`);
                } else {
                    next('/accounts');
                }
            },
        },
        {
            path: '/accounts',
            name: 'accounts',
            component: AccountsPage,
            meta: {
                title: 'navigation.apps.title',
            },
            beforeEnter: authGuard,
        },
        {
            path: '/manage-app',
            name: 'manageApp',
            component: ManageAppView,
            meta: {
                title: 'manage.app.route.name',
            },
            beforeEnter: authGuard,
        },
        {
            path: '/manage-app/services',
            name: 'services',
            component: ProServicesView,
            meta: {
                title: 'manage.services.route.name',
                hideNavbar: true,
            },
            beforeEnter: authGuard,
        },
        {
            path: '/manage-app/quick-add',
            name: 'quickAdd',
            component: QuickAddView,
            meta: {
                title: 'manage.quick.add.route.name',
                hideNavbar: true,
            },
            beforeEnter: authGuard,
        },
        {
            path: '/edit-plan',
            name: 'editPlan',
            component: EditSubscriptionView,
            meta: {
                title: 'edit.subscription.title',
                hideNavbar: true,
            },
            beforeEnter: authGuard,
        },
        {
            path: '/learn',
            name: 'learn',
            component: LearnPage,
            meta: {
                title: 'navigation.keap.id.title',
            },
            beforeEnter: authGuard,
        },
        {
            path: '/thryv-id',
            name: 'thryvId',
            component: ThryvIdPage,
            meta: {
                title: 'navigation.thryv.id.title',
            },
            beforeEnter: authGuard,
        },
        {
            path: '/authorize',
            name: 'authorize',
            component: AuthorizePage,
            meta: {
                title: 'authorize.appTitle',
                hideNavbar: true,
                isOAuth: true,
            },
            beforeEnter: authGuard,
        },
        {
            path: '/integrations/authorize',
            name: 'integrationsAuthorize',
            component: IntegrationAuthorizePage,
            meta: {
                title: 'integrationV2.authorize.appTitle',
                hideNavbar: true,
                isOAuth: true,
            },
            beforeEnter: authGuard,
        },
        {
            path: '/legacy-link',
            name: 'legacyLink',
            component: LegacyLinkRedirect,
            meta: {
                title: 'Linking account',
                hideNavbar: true,
            },
            beforeEnter: authGuard,
        },
        {
            path: '/invite',
            name: 'invite',
            component: Invite,
            meta: {
                title: 'Accept your invitation',
                isPublic: true,
                isInvite: true,
                hideNavbar: true,
            },
            props: (route) => ({ ...route.query, ...route.params }),
        },
        {
            path: '/signup',
            name: 'inviteSignup',
            component: Signup,
            meta: {
                title: 'Create your account',
                isPublic: true,
                isInvite: true,
                hideNavbar: true,
            },
            props: (route) => ({ ...route.query, ...route.params }),
        },
        {
            path: '/signup/password-instructions',
            name: 'invitePasswordInstructions',
            component: InvitePasswordInstructions,
            meta: {
                title: 'Set your password',
                isPublic: true,
                isInvite: true,
                hideNavbar: true,
            },
            props: (route) => ({ ...route.query, ...route.params }),
        },
        {
            path: '/invite/link',
            name: 'linkInvite',
            component: LinkInvite,
            meta: {
                title: 'Linking account via invitation',
                isInvite: true,
                hideNavbar: true,
            },
            props: (route) => ({ ...route.query, ...route.params }),
            beforeEnter: authGuard,
        },
        {
            path: '/invite/mfa',
            name: 'mfaInvite',
            component: MfaInvite,
            meta: {
                title: 'Select an MFA Option',
                isInvite: true,
                hideNavbar: true,
            },
            props: (route) => ({ ...route.query, ...route.params }),
            beforeEnter: authGuard,
        },
        {
            path: '/auth-error',
            name: 'authError',
            component: AuthErrorPage,
            meta: {
                title: 'authError.heading',
                isPublic: true,
                hideNavbar: true,
            },
            props: (route) => ({
                error: route.query.error || '',
                errorDescription: route.query.error_description || '',
            }),
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'page.not.found',
            component: PageNotFound,
            meta: {
                title: 'page.notFound',
                isPublic: true,
            },
            beforeEnter: authGuard,
        },
    ];
};
